import { FolderOpen, Plus } from 'lucide-react'
import { useRef } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router'

import { ClaimsTable } from '@/components/ClaimsTable'
import { OnboardingBannerSlot } from '@/components/OnboardingBannerSlot'
import { useCaiDocked } from '@/components/sidebar'
import { Button } from '@/components/ui/button'
import { GeneratedBlankState } from '@/components/ui/generated-blank-state'
import { cn } from '@/lib/utils'
import { useClaims } from '@/models/claim'
import { useFeatureFlag } from '@/models/flag'
import { hasHadCoverage, useCurrentUser, worksFromQuotesPage } from '@/models/user'
import { AskCai, AskCaiChatHandle } from '../../../components/AskCai/AskCai'

export function Claims() {
  const chatRef = useRef<AskCaiChatHandle>(null)
  const caiDocked = useCaiDocked()
  const { claims, isLoading } = useClaims()
  const lifecycleEnabled = useFeatureFlag('ENABLE_CLAIM_LIFECYCLE')
  const { user } = useCurrentUser({ enabled: true })
  const hasClaims = claims.length > 0
  // Shown before coverage so a fleet sees what's coming; there's nothing to claim on yet.
  const covered = hasHadCoverage(user?.currentOrg)
  const reportButton = covered ? (
    <Button asChild>
      <Link to="/insure/claims/new">
        <Plus />
        Report a claim
      </Link>
    </Button>
  ) : (
    <Button disabled>
      <Plus />
      Report a claim
    </Button>
  )

  return (
    <>
      <Helmet>
        <title>Claims</title>
      </Helmet>

      <h1 className="text-ink mb-4 text-2xl font-bold tracking-[-0.01em]">Claims</h1>
      <OnboardingBannerSlot className="mb-4" />
      <div className={cn('grid grid-cols-1 gap-6', hasClaims && 'lg:grid-cols-[3fr_1fr]')}>
        <div>
          {!isLoading && !hasClaims ? (
            <GeneratedBlankState icon={FolderOpen} action={reportButton}>
              {!covered &&
                'Once your coverage starts, you can report a claim here and follow it until it’s settled.'}
            </GeneratedBlankState>
          ) : (
            <ClaimsTable
              claims={claims}
              isLoading={isLoading}
              getEditHref={(claim) => `/insure/claims/${claim.id}`}
              allowWithdraw={lifecycleEnabled}
            />
          )}
        </div>

        <div className={cn('flex flex-col gap-4', !hasClaims && 'max-w-2xl')}>
          {!caiDocked && !worksFromQuotesPage(user?.currentOrg) && <AskCai ref={chatRef} />}
          {hasClaims && reportButton}
        </div>
      </div>
    </>
  )
}
